import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Bell,
  Archive,
  Compass,
  Home,
  Menu,
  MessageCircle,
  NotebookTabs,
  Orbit,
  Search,
  Settings,
} from "lucide-react";
import BlackspaceMenu from "./BlackspaceMenu";
import { fetchGroupInvites, fetchMessageRequests } from "../api/messages";
import { fetchNotificationSummary } from "../api/notifications";
import { getProfile, updateProfile, type User } from "../api/profile";
import { connectSocket } from "../api/socket";
import { applyThemeClass } from "../utils/theme";

const API_BASE: string = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(/\/$/, "");
const NOTIFICATIONS_UPDATED_EVENT = "blackspace:notifications-updated";
const MESSAGE_REQUESTS_UPDATED_EVENT = "blackspace:message-requests-updated";

const tabs = [
  { label: "Home", path: "/home", icon: Home },
  { label: "Spaces", path: "/spaces", icon: Orbit },
  { label: "Vault", path: "/notes", icon: NotebookTabs },
  { label: "Messages", path: "/message", icon: MessageCircle },
  { label: "Alerts", path: "/alerts", icon: Bell },
  { label: "Settings", path: "/settings", icon: Settings },
];

const mobilePrimaryTabs = [
  { label: "Home", path: "/home", icon: Home },
  { label: "Spaces", path: "/spaces", icon: Orbit },
  { label: "Messages", path: "/message", icon: MessageCircle },
  { label: "Vault", path: "/notes", icon: NotebookTabs },
];

const anonymousTabs = [
  { label: "Home", path: "/home", icon: Home },
  { label: "Discover", path: "/discover", icon: Compass },
  { label: "Search", path: "/search", icon: Search },
  { label: "Spaces", path: "/spaces/all", icon: Orbit },
  { label: "Archive", path: "/archive", icon: Archive },
];

export default function FooterNav() {
  const location = useLocation();
  const reopenMenuFromRoute = Boolean((location.state as { reopenBlackspaceMenu?: boolean } | null)?.reopenBlackspaceMenu);
  const [canSeeSentinel, setCanSeeSentinel] = useState(false);
  const [isPresenceIdentity, setIsPresenceIdentity] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [messageRequestCount, setMessageRequestCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profile, setProfile] = useState<User | null>(null);
  const [currentTheme, setCurrentTheme] = useState(() => localStorage.getItem("theme") || "Light");

  const toggleTheme = useCallback(async () => {
    const newTheme = currentTheme === "Light" ? "Dark" : "Light";
    setCurrentTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    applyThemeClass(newTheme);
    try {
      await updateProfile({ appearance: { theme: newTheme } });
    } catch {
      // Keep the instant local theme change even if persistence fails.
    }
  }, [currentTheme]);

  const loadSentinelAccess = useCallback(async () => {
    const token = localStorage.getItem("token");
    setCanSeeSentinel(false);
    setIsPresenceIdentity(false);
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/api/sentinel/me`, {
        cache: "no-store",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const realm = data?.realm;
      setIsPresenceIdentity(Boolean(realm?.isPresence));
      setCanSeeSentinel(Boolean(realm?.canAccessSentinel));
    } catch {
      setCanSeeSentinel(false);
      setIsPresenceIdentity(false);
    }
  }, []);

  const loadNotificationSummary = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUnreadCount(0);
      return;
    }
    try {
      const data = await fetchNotificationSummary();
      setUnreadCount(data.unreadCount);
    } catch {
      setUnreadCount(0);
    }
  }, []);

  const loadMessageRequestSummary = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setMessageRequestCount(0);
      return;
    }
    try {
      const [messageRequests, groupInvites] = await Promise.all([
        fetchMessageRequests(),
        fetchGroupInvites(),
      ]);
      setMessageRequestCount((messageRequests?.length || 0) + (groupInvites?.length || 0));
    } catch {
      setMessageRequestCount(0);
    }
  }, []);

  const loadProfile = useCallback(async () => {
    if (!localStorage.getItem("token")) {
      setProfile(null);
      return;
    }
    try {
      const data = await getProfile();
      setProfile({ ...data.user, realm: (data as any).realm, isPresence: Boolean((data as any).realm?.isPresence) });
    } catch {
      setProfile(null);
    }
  }, []);

  useEffect(() => {
    loadSentinelAccess();
    window.addEventListener("focus", loadSentinelAccess);
    window.addEventListener("storage", loadSentinelAccess);
    return () => {
      window.removeEventListener("focus", loadSentinelAccess);
      window.removeEventListener("storage", loadSentinelAccess);
    };
  }, [loadSentinelAccess]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUnreadCount(0);
      return;
    }

    const handleUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ unreadCount?: number }>).detail;
      if (typeof detail?.unreadCount === "number") {
        setUnreadCount(detail.unreadCount);
        return;
      }
      loadNotificationSummary();
    };
    const handleVisibility = () => {
      if (!document.hidden) loadNotificationSummary();
    };

    loadNotificationSummary();
    const timer = window.setInterval(loadNotificationSummary, 15000);
    window.addEventListener("focus", loadNotificationSummary);
    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, handleUpdated);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", loadNotificationSummary);
      window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, handleUpdated);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [loadNotificationSummary]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setMessageRequestCount(0);
      return;
    }

    const handleUpdated = () => loadMessageRequestSummary();
    const handleVisibility = () => {
      if (!document.hidden) loadMessageRequestSummary();
    };

    loadMessageRequestSummary();
    const timer = window.setInterval(loadMessageRequestSummary, 15000);
    window.addEventListener("focus", loadMessageRequestSummary);
    window.addEventListener(MESSAGE_REQUESTS_UPDATED_EVENT, handleUpdated);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", loadMessageRequestSummary);
      window.removeEventListener(MESSAGE_REQUESTS_UPDATED_EVENT, handleUpdated);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [loadMessageRequestSummary]);

  useEffect(() => {
    if (!localStorage.getItem("token")) return;
    let socket: ReturnType<typeof connectSocket> | null = null;

    const updateUnread = (payload: { unreadCount?: number } = {}) => {
      if (typeof payload.unreadCount !== "number") {
        loadNotificationSummary();
        return;
      }
      setUnreadCount(payload.unreadCount);
      window.dispatchEvent(new CustomEvent(NOTIFICATIONS_UPDATED_EVENT, { detail: { unreadCount: payload.unreadCount } }));
    };
    const handleNotificationNew = (payload: { unreadCount?: number; notification?: { type?: string; metadata?: Record<string, unknown> } } = {}) => {
      updateUnread(payload);
      if (payload.notification?.type === "message_request" || payload.notification?.metadata?.invite) {
        loadMessageRequestSummary();
      }
    };
    const handleRequestChanged = () => loadMessageRequestSummary();

    try {
      socket = connectSocket();
      socket.on("notification:new", handleNotificationNew);
      socket.on("notification:summary", updateUnread);
      socket.on("message:request:new", handleRequestChanged);
      socket.on("message:request:updated", handleRequestChanged);
    } catch {
      return;
    }

    return () => {
      socket?.off("notification:new", handleNotificationNew);
      socket?.off("notification:summary", updateUnread);
      socket?.off("message:request:new", handleRequestChanged);
      socket?.off("message:request:updated", handleRequestChanged);
    };
  }, [loadMessageRequestSummary, loadNotificationSummary]);

  useEffect(() => {
    loadProfile();
    window.addEventListener("focus", loadProfile);
    window.addEventListener("storage", loadProfile);
    return () => {
      window.removeEventListener("focus", loadProfile);
      window.removeEventListener("storage", loadProfile);
    };
  }, [loadProfile]);

  useEffect(() => {
    const syncTheme = () => setCurrentTheme(localStorage.getItem("theme") || "Light");
    window.addEventListener("focus", syncTheme);
    window.addEventListener("storage", syncTheme);
    return () => {
      window.removeEventListener("focus", syncTheme);
      window.removeEventListener("storage", syncTheme);
    };
  }, []);

  useEffect(() => {
    setMobileMenuOpen(reopenMenuFromRoute);
  }, [location.hash, location.pathname, location.search, reopenMenuFromRoute]);

  const visibleTabs = tabs;

  if (!localStorage.getItem("token")) {
    return (
      <div className="bs-footer-nav bs-footer-nav-anonymous border-t border-neutral-800 bg-black/95 px-2 py-3 text-sm text-neutral-400 backdrop-blur">
        <nav className="bs-anonymous-footer-nav mx-auto grid w-full max-w-3xl grid-cols-5 items-center gap-1">
          {anonymousTabs.map(({ label, path, icon: Icon }) => {
            const active = path.startsWith("/spaces")
              ? location.pathname.startsWith("/spaces")
              : location.pathname === path;
            return (
              <Link key={path} to={path} className={`bs-footer-nav-item flex min-w-0 flex-col items-center justify-center gap-1 rounded-lg px-1 py-2 transition ${active ? "is-active" : ""}`}>
                <Icon size={19} className={active ? "text-cyan-300" : "text-neutral-500"} />
                <span className="max-w-full truncate text-[10px] font-black leading-tight sm:text-xs">{label}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    );
  }

  return (
    <div className={`bs-footer-nav ${mobileMenuOpen ? "bs-footer-nav-open" : ""} border-t border-neutral-800 bg-black/95 px-2 py-3 text-sm text-neutral-400 backdrop-blur`}>
      <BlackspaceMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        variant="sheet"
        profile={profile}
        unreadCount={unreadCount}
        canSeeSentinel={canSeeSentinel}
        isPresenceIdentity={isPresenceIdentity}
        currentTheme={currentTheme}
        onToggleTheme={toggleTheme}
      />

      <nav className="bs-desktop-footer-nav mx-auto flex max-w-3xl items-center justify-around gap-1">
        {visibleTabs.map(({ label, path, icon: Icon }) => {
          const active = location.pathname === path;
          const showBadge = path === "/alerts" && unreadCount > 0;
          const showRequestDot = path === "/message" && messageRequestCount > 0;
          return (
            <Link
              key={path}
              to={path}
              className={`bs-footer-nav-item flex min-w-0 items-center gap-2 rounded-md px-3 py-2 transition-colors ${active ? "is-active" : ""}`}
            >
              <span className="bs-footer-icon-wrap relative inline-grid place-items-center">
                <Icon size={17} className={active ? "text-cyan-300" : "text-neutral-500"} />
                {showBadge && (
                  <span className="absolute -right-2 -top-2 grid min-h-4 min-w-4 place-items-center rounded-sm border border-black bg-cyan-500 px-1 text-[10px] font-black leading-none text-black">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
                {showRequestDot && (
                  <span className="bs-footer-request-dot absolute -right-1 -top-1" aria-label={`${messageRequestCount} pending request${messageRequestCount === 1 ? "" : "s"}`} />
                )}
              </span>
              <span className="bs-footer-label hidden truncate font-medium sm:inline">{label}</span>
            </Link>
          );
        })}
      </nav>

      <nav className="bs-mobile-footer-nav grid w-full grid-cols-5 items-center md:hidden">
        {mobilePrimaryTabs.map(({ label, path, icon: Icon }) => {
          const routeActive = path === "/spaces" ? location.pathname.startsWith("/spaces") : location.pathname === path;
          const active = !mobileMenuOpen && routeActive;
          const showRequestDot = path === "/message" && messageRequestCount > 0;
          return (
            <Link
              key={path}
              to={path}
              onClick={(event) => {
                if (!mobileMenuOpen) return;
                event.preventDefault();
                setMobileMenuOpen(false);
              }}
              className={`bs-footer-nav-item flex min-w-0 w-full flex-col items-center justify-center gap-1 rounded-lg px-1.5 py-2 transition ${active ? "is-active" : ""}`}
            >
              <span className="bs-footer-icon-wrap relative inline-grid place-items-center">
                <Icon size={19} />
                {showRequestDot && (
                  <span className="bs-footer-request-dot absolute -right-1 -top-1" aria-label={`${messageRequestCount} pending request${messageRequestCount === 1 ? "" : "s"}`} />
                )}
              </span>
              <span className="bs-footer-label max-w-full truncate text-[10px] font-black leading-[1.25]">{label}</span>
            </Link>
          );
        })}
        <button
          type="button"
          onClick={() => setMobileMenuOpen((open) => !open)}
          className={`bs-footer-nav-item relative flex min-w-0 w-full flex-col items-center justify-center gap-1 rounded-lg px-1.5 py-2 transition ${mobileMenuOpen ? "is-active" : ""}`}
          aria-label="Open mobile menu"
          aria-expanded={mobileMenuOpen}
        >
          <span className="bs-footer-icon-wrap relative inline-grid place-items-center">
            <Menu size={19} />
          </span>
          <span className="bs-footer-label max-w-full truncate text-[10px] font-black leading-[1.25]">Menu</span>
          {unreadCount > 0 && (
            <span className="bs-mobile-footer-badge absolute grid place-items-center rounded-sm bg-cyan-300 px-1 text-[9px] font-black text-black">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </nav>
    </div>
  );
}
